import * as d3 from 'd3';
import { Position, BlockGeometry, Block } from '../../types';
import { snapToGrid } from '../../utils/gridUtils';
import { createAlignmentGuides, findAlignments, updateGuides, hideGuides } from '../../utils/alignmentGuides';

function extractBlockIdFromClass(classAttr: string): string | null {
  const match = classAttr.match(/(?:process|decision|start|end|note)-block(?:-container)?-(.+)/);
  if (!match) return null;
  const id = match[1].trim();
  if (!id || id.includes(' block')) return null;
  return id;
}

function blockContainerSelector(blockId: string): string {
  return `.process-block-container-${blockId}, .decision-block-container-${blockId}, .start-block-container-${blockId}, .end-block-container-${blockId}, .note-block-container-${blockId}`;
}

export function implementDrag(
  element: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  elementId: string,
  blocks: Block[],
  onDragEnd: (id: string, position: BlockGeometry) => void,
  setBlocks: React.Dispatch<React.SetStateAction<Block[]>>,
  onDragMove?: (updates: Array<{ id: string; position: Position }>) => void,
  getSelectedBlockIds?: () => string[]
) {
  const container = d3.select(element.node()?.parentNode as HTMLElement);
  const { verticalGuide, horizontalGuide } = createAlignmentGuides(container as unknown as d3.Selection<HTMLDivElement, unknown, null, undefined>);

  const deselectAllBlocks = () => {
    container.selectAll('[class*="-block-container-"].block-selected')
      .classed('block-selected', false)
      .style('box-shadow', 'none');
    container.selectAll('.process-block.block-selected, .decision-block.block-selected, .start-block.block-selected, .end-block.block-selected, .note-block.block-selected')
      .classed('block-selected', false)
      .style('box-shadow', 'none');
  };

  const selectBlockVisually = (target: d3.Selection<HTMLDivElement, unknown, null, undefined>) => {
    const inner = target.select('.process-block, .decision-block, .start-block, .end-block, .note-block');
    target.classed('block-selected', true);
    if (!inner.empty()) {
      inner.classed('block-selected', true);
      inner.style('box-shadow', '0 0 0 2px rgba(59, 130, 246, 0.5)');
    } else {
      target.style('box-shadow', '0 0 0 2px rgba(59, 130, 246, 0.5)');
    }
  };

  const collectSelectedBlockIdsFromDom = (): string[] => {
    const ids: string[] = [];
    container.selectAll('.block-selected').each(function () {
      const blockId = extractBlockIdFromClass(d3.select(this).attr('class') || '');
      if (blockId && !ids.includes(blockId) && !container.select(blockContainerSelector(blockId)).empty()) {
        ids.push(blockId);
      }
    });
    return ids;
  };

  const isBlockVisuallySelected = (blockId: string): boolean => {
    const blockContainer = container.select(blockContainerSelector(blockId));
    if (blockContainer.empty()) return false;
    if (blockContainer.classed('block-selected')) return true;
    return !blockContainer.select('.block-selected').empty();
  };

  // Remove any existing container click handlers
  container.on('click.deselect', null);

  // Add container-level click handler for deselection
  container.on('click.deselect', function (event: MouseEvent) {
    // Stop if we're in a drag operation
    if (event.defaultPrevented) return;

    const target = event.target as HTMLElement;

    // Check if the click is directly on the container (not on a block or its children)
    if (target === container.node() ||
      (target.closest('.diagram-container') &&
        !target.closest('.block') &&
        !target.closest('[class^="block-selection-menu-"]') &&
        !target.closest('.connection-point'))) {

      // Deselect all blocks
      deselectAllBlocks();

      // Update blocks state to deselect all
      setBlocks && setBlocks(prevBlocks =>
        prevBlocks.map(block => ({
          ...block,
          selected: false
        }))
      );

      // Remove all menus and connection points
      container.selectAll('[class^="block-selection-menu-"]').remove();
      container.selectAll('.connection-point').remove();
    }
  });

  // Store initial positions of selected blocks for multi-drag
  let selectedBlocksInitialPositions: Map<string, Position> = new Map();
  let activeDragBlockIds: string[] = [];
  let dragOffset: Position = { x: 0, y: 0 };
  let dragMoveRaf: number | null = null;
  let pendingDragMoveUpdates: Array<{ id: string; position: Position }> | null = null;

  const scheduleDragMove = (updates: Array<{ id: string; position: Position }>) => {
    if (!onDragMove) return;
    pendingDragMoveUpdates = updates;
    if (dragMoveRaf != null) return;
    dragMoveRaf = requestAnimationFrame(() => {
      dragMoveRaf = null;
      if (pendingDragMoveUpdates) {
        onDragMove(pendingDragMoveUpdates);
        pendingDragMoveUpdates = null;
      }
    });
  };

  const cancelDragMove = () => {
    if (dragMoveRaf != null) {
      cancelAnimationFrame(dragMoveRaf);
      dragMoveRaf = null;
    }
    pendingDragMoveUpdates = null;
  };

  const drag = d3.drag<HTMLDivElement, unknown>()
    .filter((event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.block-resize-handle')) return false;
      return !event.ctrlKey && !event.button;
    })
    .on('start', (event) => {
      event.sourceEvent.stopPropagation();

      const sourceEvent = event.sourceEvent as MouseEvent;
      const multiSelectKey = sourceEvent.ctrlKey || sourceEvent.metaKey;
      const selectedFromDom = collectSelectedBlockIdsFromDom();
      const selectedFromState = getSelectedBlockIds?.() ?? [];
      const selectedMerged = [...new Set([...selectedFromDom, ...selectedFromState])];
      const isCurrentInSelection = selectedMerged.includes(elementId) || isBlockVisuallySelected(elementId);

      let selectedBlockIds: string[];
      if (multiSelectKey) {
        selectedBlockIds = [...new Set([...selectedMerged, elementId])];
      } else if (selectedMerged.length > 1 && isCurrentInSelection) {
        // Ctrl+click multi-select, then drag without modifier
        selectedBlockIds = selectedMerged;
      } else {
        // Single-select drag: only move the block being dragged
        deselectAllBlocks();
        selectBlockVisually(element);
        setBlocks((prevBlocks) =>
          prevBlocks.map((block) => ({
            ...block,
            selected: block.id === elementId,
          }))
        );
        selectedBlockIds = [elementId];
      }

      if (!selectedBlockIds.includes(elementId)) {
        selectedBlockIds.push(elementId);
      }

      activeDragBlockIds = selectedBlockIds;

      // Store initial positions of all selected blocks
      selectedBlocksInitialPositions.clear();
      selectedBlockIds.forEach((blockId) => {
        const containerElement = container.select(blockContainerSelector(blockId));
        if (!containerElement.empty()) {
          const leftStyle = containerElement.style('left');
          const topStyle = containerElement.style('top');
          const initialX = leftStyle ? parseFloat(leftStyle) : 0;
          const initialY = topStyle ? parseFloat(topStyle) : 0;
          selectedBlocksInitialPositions.set(blockId, { x: initialX, y: initialY });
        }
      });

      dragOffset = { x: 0, y: 0 };

      // Apply transform to all selected blocks
      selectedBlockIds.forEach((blockId) => {
        const blockElement = blockId === elementId
          ? element
          : container.select(blockContainerSelector(blockId));

        if (!blockElement.empty()) {
          blockElement
            .style('transform', 'scale(1.02)')
            .style('transition', 'transform 0.1s ease');
        }
      });

      // Remove menu for dragged block; keep connection points so they stay synced during drag
      container.select(`[class^="block-selection-menu-${elementId}"]`).remove();
    })
    .on('drag', (event) => {
      const currentBlock = blocks.find(b => b.id === elementId);
      if (!currentBlock) return;

      // Accumulate the drag offset (event.dx and event.dy are incremental)
      dragOffset.x += event.dx;
      dragOffset.y += event.dy;

      // Get the initial position of the dragged block
      const initialPos = selectedBlocksInitialPositions.get(elementId);
      if (!initialPos) {
        return;
      }
      
      const newX = initialPos.x + dragOffset.x;
      const newY = initialPos.y + dragOffset.y;

      // Calculate alignment guides for the main block
      const { guides, snappedPosition } = findAlignments(
        currentBlock,
        blocks,
        { x: newX, y: newY }
      );

      updateGuides(verticalGuide, horizontalGuide, guides);

      // Move all selected blocks together
      const liveUpdates: Array<{ id: string; position: Position }> = [];

      selectedBlocksInitialPositions.forEach((initialPos, blockId) => {
        const block = blocks.find(b => b.id === blockId);
        if (!block || !initialPos) {
          return;
        }

        const blockNewX = initialPos.x + dragOffset.x;
        const blockNewY = initialPos.y + dragOffset.y;

        // For the main dragged block, use snapped position
        if (blockId === elementId) {
          element
            .style('left', `${snappedPosition.x}px`)
            .style('top', `${snappedPosition.y}px`);
          
          currentBlock.x = snappedPosition.x;
          currentBlock.y = snappedPosition.y;
          liveUpdates.push({ id: blockId, position: snappedPosition });
        } else {
          // For other selected blocks, move them by the same offset
          const containerElement = container.select(blockContainerSelector(blockId));
          
          if (!containerElement.empty()) {
            containerElement
              .style('left', `${blockNewX}px`)
              .style('top', `${blockNewY}px`);
          }

          // Update block position in memory
          block.x = blockNewX;
          block.y = blockNewY;
          liveUpdates.push({ id: blockId, position: { x: blockNewX, y: blockNewY } });
        }
      });

      scheduleDragMove(liveUpdates);
    })
    .on('end', (event) => {
      cancelDragMove();
      event.sourceEvent.stopPropagation();

      // Get the initial position of the dragged block
      const initialPos = selectedBlocksInitialPositions.get(elementId);
      if (!initialPos) {
        return;
      }

      // Calculate final position using accumulated drag offset
      const finalX = initialPos.x + dragOffset.x;
      const finalY = initialPos.y + dragOffset.y;
      const snappedPosition = snapToGrid({ x: finalX, y: finalY });

      // Calculate the final offset after snapping (this is the actual movement)
      const finalOffset = {
        x: snappedPosition.x - initialPos.x,
        y: snappedPosition.y - initialPos.y
      };

      // Update all selected blocks with snapped positions
      const updatedBlocks: Array<{ id: string; position: Position }> = [];

      selectedBlocksInitialPositions.forEach((initialPos, blockId) => {
        const block = blocks.find(b => b.id === blockId);
        if (!block) return;

        let finalPosition: Position;
        
        if (blockId === elementId) {
          finalPosition = snappedPosition;
        } else {
          const blockNewX = initialPos.x + finalOffset.x;
          const blockNewY = initialPos.y + finalOffset.y;
          finalPosition = snapToGrid({ x: blockNewX, y: blockNewY });
        }

        // Update DOM
        if (blockId === elementId) {
          element
            .style('left', `${finalPosition.x}px`)
            .style('top', `${finalPosition.y}px`)
            .style('transform', 'scale(1)')
            .style('transition', 'transform 0.1s ease');
        } else {
          const containerElement = container.select(blockContainerSelector(blockId));
          
          if (!containerElement.empty()) {
            containerElement
              .style('left', `${finalPosition.x}px`)
              .style('top', `${finalPosition.y}px`)
              .style('transform', 'scale(1)')
              .style('transition', 'transform 0.1s ease');
          }
        }

        updatedBlocks.push({ id: blockId, position: finalPosition });
      });

      hideGuides(verticalGuide, horizontalGuide);

      // Update React state first with all new positions
      setBlocks && setBlocks(prevBlocks =>
        prevBlocks.map(block => {
          const updatedBlock = updatedBlocks.find(ub => ub.id === block.id);
          if (updatedBlock) {
            return {
              ...block,
              x: updatedBlock.position.x,
              y: updatedBlock.position.y,
              selected: activeDragBlockIds.includes(block.id),
            };
          }
          return block;
        })
      );
      
      // Then call onDragEnd for each moved block (this will update the ProcessManager)
      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        updatedBlocks.forEach(({ id, position }) => {
          onDragEnd(id, position);
        });
      });
    });

  element.call(drag);

  // Handle block clicks with Ctrl/Cmd key for multiple selection
  element.on('click.dragUtils', (event: Event) => {
    // Don't interfere if event was already handled
    if (event.defaultPrevented) return;
    
    event.stopPropagation();
    const e = event as MouseEvent;
    const multiSelect = e.ctrlKey || e.metaKey;

    // Check current selection state
    const isCurrentlySelected = element.classed('block-selected');
    const blockElement = element.select('.process-block, .decision-block, .start-block, .end-block, .note-block');
    const isBlockSelected = !blockElement.empty() && blockElement.classed('block-selected');

    if (!multiSelect) {
      // Single selection - deselect all other blocks
      deselectAllBlocks();
      selectBlockVisually(element);

      setBlocks(prevBlocks =>
        prevBlocks.map(block => ({
          ...block,
          selected: block.id === elementId
        }))
      );
    } else {
      // Multiple selection - toggle current block
      // Check if block is currently selected (either container or inner block)
      const isSelected = isCurrentlySelected || isBlockSelected;
      const willBeSelected = !isSelected;

      // Toggle visual selection
      element.classed('block-selected', willBeSelected);
      if (!blockElement.empty()) {
        blockElement.classed('block-selected', willBeSelected);
        blockElement.style('box-shadow', willBeSelected ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none');
      } else {
        element.style('box-shadow', willBeSelected ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none');
      }

      setBlocks(prevBlocks =>
        prevBlocks.map(block => ({
          ...block,
          selected: block.id === elementId ? willBeSelected : block.selected
        }))
      );
    }
  });

  // Ensure block class is present
  element.classed('block', true);

  // Ensure container has the diagram-container class
  container.classed('diagram-container', true);
}
